<template>
  <ResponsiveLayout :progress="progress">
    <template
      v-if="canGoBack"
      #header_start
    >
      <ButtonBack @click="navigateBack" />
    </template>
    <template #header_end><HeaderHelp /></template>
    <div class="insurance-details">
      <h2
        class="insurance-details__header"
        v-html="$t('eu__insurance_details__header')"
      />
      <CorporateHeader :corporate="corporate" />
      <div
        class="insurance-details__description"
        v-html="description"
      />

      <div class="insurance-details__contacts">
        <InsuranceContact
          :contact-data="getCorporateContactData"
          @cta="handleInsuranceContactCTAEvent"
        />
      </div>
    </div>
  </ResponsiveLayout>
</template>

<script setup>
import { computed } from 'vue'
import useCorporateStore from '@shared/stores/useCorporateStore.js'
import InsuranceContact from '@eu/app/components/InsuranceContact.vue'
import publicInsuranceContacts from '@eu/app/config/public-insurance-contacts.json'
import privateInsuranceContacts from '@eu/app/config/private-insurance-contacts.json'
import { useI18n } from 'petite-vue-i18n'
import ButtonBack from '@shared/components/ButtonBack.vue'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import HeaderHelp from '@eu/app/components/HeaderHelp.vue'
import CorporateHeader from '@shared/components/CorporateHeader.vue'
import useNavigation from '@eu/app/composables/useNavigation'
import ROUTE from '@eu/app/router/appModule/names'
import useOnboardingProgress from '@eu/app/composables/useOnboardingProgress.js'

const { t } = useI18n()
const corporateStore = useCorporateStore()
const { navigateBack, canGoBack } = useNavigation(ROUTE.INSURANCE_DETAILS)
const { progress } = useOnboardingProgress()

const corporate = computed(() => corporateStore.corporate)
const appName = computed(() => t('eu__generic__app'))

const description = computed(() =>
  corporate.value.insuranceType === 'private'
    ? t('eu__insurance_details__private__description', {
        app_name: appName.value,
      })
    : t('eu__insurance_details__public__description', {
        app_name: appName.value,
      }),
)
const getCorporateContactData = computed(() => ({
  actualItems: corporate.value.primaryAddress,
  allowedItems:
    corporate.value.insuranceType === 'private'
      ? privateInsuranceContacts
      : publicInsuranceContacts,
}))

const handleInsuranceContactCTAEvent = (data) => {
  if (data.item.key === 'email') {
    requestInvoiceByEmail(data.actualItems.email)
  } else if (data.item.key === 'phone') {
    requestInvoiceByPhone(data.actualItems.phone)
  }
}

const requestInvoiceByEmail = (emailAddress) => {
  const emailSubject = t('eu__insurance_details__email_subject', {
    app_name: appName.value,
  })
  const emailBody = t('eu__insurance_details__email_body')
  const link = document.createElement('a')
  link.setAttribute(
    'href',
    `mailto:${emailAddress}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`,
  )
  link.click()
}

const requestInvoiceByPhone = (phoneNumber) => {
  const link = document.createElement('a')
  link.setAttribute('href', `tel:${phoneNumber}`)
  link.click()
}
</script>

<style lang="scss">
.insurance-details {
  width: 100%;
  max-width: 784px;
  box-sizing: border-box;

  &__header {
    @include kds-typography-title-xlarge;
    margin: 0 0 16px;
  }

  &__description {
    @include kds-typography-paragraph-large;
    color: $kds-color-text-secondary;
    margin: 16px 0 0;
  }

  &__logo {
    max-width: 100%;
    max-height: 100px;
    margin: 8px 0 16px;
  }
}
</style>
